import { useEffect, useRef, useCallback } from 'react';
import { useOidcAuth } from '@/hooks/useOidcAuth';
import { useAuthStore } from '@/stores/authStore';
import { authService } from '@/services/auth';
import { isOidcEnabled } from '@/auth/oidcClient';
import { logger } from '@/utils/logger';
import axios from 'axios';

export const OidcSessionSync: React.FC = () => {
  const oidc = useOidcAuth();
  const { user, setAuth, clearAuth } = useAuthStore();

  // Use refs to prevent dependency loops and track state without triggering re-renders
  const lastTokenRef = useRef<string | null>(null);
  const lastSyncedEmailRef = useRef<string | null>(null);
  const isSyncingRef = useRef(false);
  const syncAttemptedRef = useRef(false);
  // Timestamp of last exchange attempt — acts as a hard mutex preventing
  // rapid re-exchanges regardless of why the effect re-runs. Minimum 3 seconds
  // between attempts protects against rate-limiting and retry loops.
  const lastExchangeAtRef = useRef<number>(0);
  // Tracks whether we have already successfully exchanged an OIDC token with
  // the backend in this browser session. Initialized from sessionStorage so
  // page navigations (which remount this component) do not re-exchange.
  const backendSessionValidRef = useRef<boolean>(
    Boolean(sessionStorage.getItem('access_token') && sessionStorage.getItem('refresh_token'))
  );

  // Memoize clearAuth to prevent unnecessary effect triggers
  const handleClearAuth = useCallback(() => {
    lastTokenRef.current = null;
    lastSyncedEmailRef.current = null;
    syncAttemptedRef.current = false;
    backendSessionValidRef.current = false;
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('refresh_token');
    sessionStorage.removeItem('oidc_login_error');
    clearAuth();
  }, [clearAuth]);

  useEffect(() => {
    if (!isOidcEnabled()) {
      return;
    }

    // Wait for OIDC to finish loading
    if (oidc.isLoading) {
      return;
    }

    // Handle unauthenticated state
    if (!oidc.isAuthenticated || !oidc.user?.access_token) {
      handleClearAuth();
      return;
    }

    const accessToken = oidc.user.access_token;
    const oidcEmail = oidc.user.profile?.email || null;

    // Check if token actually changed (not just a re-render)
    const tokenChanged = lastTokenRef.current !== accessToken;

    // OIDC token changed — could be initial login, silent renewal, or user switch.
    if (tokenChanged) {
      lastTokenRef.current = accessToken;

      // Only invalidate the backend session if this is a new user (different email)
      // or we never had a backend session. Silent token renewal for the SAME user
      // does NOT invalidate our backend JWT, so don't clear user / re-exchange.
      const isUserChange = lastSyncedEmailRef.current && lastSyncedEmailRef.current !== oidcEmail;
      const noBackendSessionYet = !backendSessionValidRef.current;
      if (isUserChange || noBackendSessionYet) {
        backendSessionValidRef.current = false;
        useAuthStore.setState({ user: null });
        sessionStorage.setItem('access_token', accessToken);
        sessionStorage.removeItem('refresh_token');
        syncAttemptedRef.current = false;
        logger.debug('OIDC token changed (initial or user switch), invalidating backend session');
      } else {
        logger.debug('OIDC token rotated (silent renewal), keeping backend session');
      }
    }

    // If backend session already exists and user data is loaded, skip re-exchange.
    // This prevents redundant OIDC calls on page navigation and silent renew cycles.
    if (backendSessionValidRef.current && user) {
      return;
    }

    // Determine if we need to sync user data with backend
    // Only sync if:
    // 1. We haven't synced yet with this email
    // 2. The email changed (different user logged in)
    // 3. We don't have user data yet
    // 4. We don't already have a valid backend session
    const needsUserSync =
      !syncAttemptedRef.current &&
      !backendSessionValidRef.current &&
      (lastSyncedEmailRef.current !== oidcEmail || !user);

    // Skip if already syncing or no sync needed
    if (isSyncingRef.current || !needsUserSync) {
      return;
    }

    // Hard mutex: never trigger an exchange more than once every 5 seconds.
    // Uses sessionStorage so it persists across component remounts (which can
    // happen if a parent like SetupCheck re-enters its loading state).
    const now = Date.now();
    const lastExchangeAt = parseInt(sessionStorage.getItem('oidc_last_exchange_at') || '0', 10);
    if (now - lastExchangeAt < 5000) {
      return;
    }
    sessionStorage.setItem('oidc_last_exchange_at', String(now));
    lastExchangeAtRef.current = now;

    // Mark as syncing and attempted
    isSyncingRef.current = true;
    syncAttemptedRef.current = true;

    // NOTE: we intentionally do NOT use an AbortController here.
    // The component can remount (e.g. SetupCheck toggling loading state) and
    // the cleanup would abort a successful in-flight exchange, losing the
    // response. The sessionStorage mutex above already prevents duplicate
    // exchanges, so abort isn't needed.
    logger.debug('Exchanging OIDC token with backend');

    // First exchange the OIDC token with backend to create a session
    authService.loginWithOidc(accessToken)
      .then((authResponse) => {
        // Always persist the backend tokens to sessionStorage even if the
        // component unmounted (aborted) during the request — otherwise we
        // lose the exchange and get stuck on "Loading user data..." forever.
        try {
          sessionStorage.removeItem('oidc_login_error');
        } catch {
          // Ignore storage failures for non-critical cleanup.
        }

        // Store the backend tokens (not the OIDC token)
        const backendAccessToken = authResponse.access_token;
        const backendRefreshToken = authResponse.refresh_token;

        if (backendAccessToken) {
          sessionStorage.setItem('access_token', backendAccessToken);
          lastTokenRef.current = backendAccessToken;
        }
        if (backendRefreshToken) {
          sessionStorage.setItem('refresh_token', backendRefreshToken);
        }

        // Mark backend session as valid — subsequent navigations will skip re-exchange
        backendSessionValidRef.current = true;
        lastSyncedEmailRef.current = oidcEmail;

        // Use the user from auth response if available, otherwise the token
        const userFromResponse = authResponse.user;
        if (userFromResponse) {
          setAuth(userFromResponse, backendAccessToken || accessToken, backendRefreshToken || null);
          logger.debug('OIDC session synced successfully with backend tokens');
        } else {
          // Fallback to getCurrentUser if user not in response
          authService.getCurrentUser()
            .then((currentUser) => {
              setAuth(currentUser, backendAccessToken || accessToken, backendRefreshToken || null);
              logger.debug('OIDC session synced successfully after fetching user');
            })
            .catch((err) => {
              logger.error('Failed to fetch user after OIDC login:', err);
              handleClearAuth();
            });
        }
      })
      .catch((error) => {
        logger.error('Failed to exchange OIDC token with backend:', error);
        const status = axios.isAxiosError(error) ? error.response?.status : undefined;
        if (status === 429) {
          // Rate-limited. Do NOT retry — retries amplify the problem.
          // Keep syncAttemptedRef=true so this effect won't re-trigger the
          // exchange on the next render. The user can refresh the page once
          // the rate-limit window clears (typically ~1 minute).
          logger.warn('OIDC backend exchange rate-limited (429) — will not retry automatically');
          return;
        }
        if (status === 401 || status === 403) {
          try {
            const reason = status === 403 ? 'access_denied' : 'session_expired';
            sessionStorage.setItem('oidc_login_error', reason);
          } catch {
            // Ignore storage failures.
          }
          void oidc.removeUser();
        }
        handleClearAuth();
      })
      .finally(() => {
        isSyncingRef.current = false;
      });
  // Minimal dependency array - only react to actual OIDC state changes
  // Using refs for tracking prevents infinite loops
  }, [oidc.isAuthenticated, oidc.isLoading, oidc.user?.access_token, oidc.user?.profile?.email, handleClearAuth, setAuth]);

  return null;
};
